"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_1 = require("react");
const Flex_1 = require("./Flex");
const Icon_1 = require("./Icon");
const Link_1 = require("./Link");
const Loader_1 = require("./Loader");
const constants_1 = require("../constants");
const utils_1 = require("../utils");
const BUTTON_DISABLED_COLOR = constants_1.Color.DARK_GREY;
const getColors = ({ design, text }) => {
    const colors = getColorsByDesign(design);
    if (text) {
        const color = design ? colors.backgroundColor : constants_1.Color.DARK_GREY;
        return {
            backgroundColor: 'transparent',
            color,
            hoverBackgroundColor: 'inherit',
            hoverColor: color,
        };
    }
    return colors;
};
const getColorsByDesign = (design) => {
    switch (design) {
        case 'cta':
            return {
                backgroundColor: constants_1.theme.color.cta,
                color: constants_1.Color.WHITE,
                hoverBackgroundColor: constants_1.Color.DARK_GREEN,
                hoverColor: constants_1.Color.WHITE,
            };
        case 'danger':
            return {
                backgroundColor: constants_1.Color.RED,
                color: constants_1.Color.WHITE,
                hoverBackgroundColor: constants_1.Color.DARK_RED,
                hoverColor: constants_1.Color.WHITE,
            };
        case 'danger-inverted':
            return {
                backgroundColor: constants_1.Color.WHITE,
                color: constants_1.Color.RED,
                hoverBackgroundColor: constants_1.Color.LIGHTER_RED,
                hoverColor: constants_1.Color.RED,
            };
        case 'dark-green':
            return {
                backgroundColor: constants_1.Color.DARK_GREEN,
                color: constants_1.Color.WHITE,
                hoverBackgroundColor: constants_1.Color.GREEN,
                hoverColor: constants_1.Color.WHITE,
            };
        case 'dark-green-inverted':
            return {
                backgroundColor: constants_1.Color.WHITE,
                color: constants_1.Color.DARK_GREEN,
                hoverBackgroundColor: constants_1.Color.LIGHTER_GREEN,
                hoverColor: constants_1.Color.DARK_GREEN,
            };
        case 'success':
            return {
                backgroundColor: constants_1.Color.LIGHTER_GREEN,
                color: constants_1.Color.DARK_GREEN,
                hoverBackgroundColor: constants_1.Color.GREEN,
                hoverColor: constants_1.Color.WHITE,
            };
        case 'inverted':
            return {
                backgroundColor: constants_1.Color.WHITE,
                color: constants_1.Color.BLUE,
                hoverBackgroundColor: constants_1.Color.LIGHTEST_BLUE,
                hoverColor: constants_1.Color.BLUE,
            };
        case 'info':
            return {
                backgroundColor: constants_1.Color.LIGHTER_BLUE,
                color: constants_1.Color.DARK_BLUE,
                hoverBackgroundColor: constants_1.Color.BLUE,
                hoverColor: constants_1.Color.WHITE,
            };
        case 'light-grey':
            return {
                backgroundColor: constants_1.Color.LIGHT_GREY,
                color: constants_1.Color.BLACK,
                hoverBackgroundColor: constants_1.Color.LEGACY_GRAY,
                hoverColor: constants_1.Color.BLACK,
            };
        case 'primary-inverted':
            return {
                backgroundColor: constants_1.Color.WHITE,
                color: constants_1.Color.BLUE,
                hoverBackgroundColor: constants_1.Color.LIGHTEST_BLUE,
                hoverColor: constants_1.Color.BLUE,
            };
        case 'warning':
            return {
                backgroundColor: constants_1.Color.LIGHTER_RED,
                color: constants_1.Color.DARK_RED,
                hoverBackgroundColor: constants_1.Color.LIGHT_RED,
                hoverColor: constants_1.Color.DARK_RED,
            };
        case 'white':
            return {
                backgroundColor: constants_1.Color.WHITE,
                color: constants_1.Color.BLACK,
                hoverBackgroundColor: constants_1.Color.LIGHTEST_BLUE,
                hoverColor: constants_1.Color.BLACK,
            };
        case 'transparent-light':
            return {
                backgroundColor: 'transparent',
                color: constants_1.Color.TRANSPARENT_DARKER_GREY,
                hoverBackgroundColor: constants_1.Color.TRANSPARENT_GREY,
                hoverColor: constants_1.Color.BLACK,
            };
        case 'primary':
        default:
            return {
                backgroundColor: constants_1.theme.color.primary,
                color: constants_1.Color.WHITE,
                hoverBackgroundColor: constants_1.Color.DARK_BLUE,
                hoverColor: constants_1.Color.WHITE,
            };
    }
};
const ButtonElement = styled_1.default.button((props) => {
    const { design, nowrap, rounded, size, text } = props;
    const hasBorder = !text &&
        (props.border === 'none' ||
            design === 'white' ||
            design === 'light-grey' ||
            design?.endsWith('-inverted'));
    const disabledProps = {
        backgroundColor: constants_1.Color.MEDIUM_GREY,
        color: BUTTON_DISABLED_COLOR,
    };
    const dimensions = constants_1.BUTTON_SIZE_MAP[size ?? 'md'];
    const paddingX = text ? 0 : rounded ? 10 : dimensions.paddingX;
    const paddingY = rounded ? 6 : dimensions.paddingY;
    const designProps = getColors(props);
    return {
        ...(0, constants_1.buttonStyles)(props),
        backgroundColor: designProps.backgroundColor,
        ...(hasBorder ? {} : { border: '1px solid transparent' }),
        ...(rounded ? { borderRadius: 999 } : {}),
        boxShadow: hasBorder ? constants_1.theme.boxShadow.normal : undefined,
        boxSizing: 'border-box',
        color: designProps.color,
        display: 'inline-block',
        fontWeight: constants_1.theme.font.weight.bold,
        textAlign: 'center',
        paddingBottom: paddingY,
        paddingLeft: paddingX,
        paddingRight: paddingX,
        paddingTop: paddingY,
        whiteSpace: nowrap ? 'nowrap' : undefined,
        width: props.fullWidth ? '100%' : undefined,
        '&:disabled': {
            ...disabledProps,
            cursor: 'not-allowed',
        },
        '&:hover:not(:disabled)': {
            backgroundColor: designProps.hoverBackgroundColor,
            color: designProps.hoverColor,
            filter: designProps.filter,
        },
        ...(0, utils_1.createStyles)(props.styles),
    };
});
const ContentWrapper = styled_1.default.div((props) => ({
    visibility: props.isLoading ? 'hidden' : 'visible',
}));
const LoaderWrapper = styled_1.default.div({
    left: '50%',
    pointerEvents: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'scale(0.65) translate(-75%, -50%)',
    width: '100%',
    height: '100%',
});
exports.Button = (0, react_1.forwardRef)((props, ref) => {
    const { children, fullWidth, loading, openNewTab, to, ...buttonProps } = props;
    const disabled = props.disabled || !!loading;
    const iconSize = buttonProps.size === 'xxs' ||
        buttonProps.size === 'xs' ||
        buttonProps.size === 'sm'
        ? 'sm'
        : 'md';
    return ((0, jsx_runtime_1.jsxs)(ButtonElement, { ...buttonProps, as: to && !disabled ? Link_1.Link : undefined, openNewTab: openNewTab, to: to, ref: ref, disabled: disabled, fullWidth: fullWidth, styles: {
            // Keep the button height consistent with icon-only buttons:
            lineHeight: `${constants_1.theme.elements.icon.size[iconSize]}px`,
            ...buttonProps.styles,
        }, children: [loading && ((0, jsx_runtime_1.jsx)(LoaderWrapper, { children: (0, jsx_runtime_1.jsx)(Loader_1.Loader, { color: BUTTON_DISABLED_COLOR }) })), (0, jsx_runtime_1.jsx)(ContentWrapper, { isLoading: !!loading, children: props.icon ? ((0, jsx_runtime_1.jsxs)(Flex_1.Flex, { gap: 2, items: "center", justify: "center", children: [(0, jsx_runtime_1.jsx)(Icon_1.Icon, { name: props.icon, size: iconSize, design: props.iconDesign }), (!!children || children === 0) && (0, jsx_runtime_1.jsx)("div", { children: children })] })) : (children) })] }));
});
exports.Button.displayName = 'Button';
